/**
 * ATTEST-UI-1 — the letter generator, mounted inside the QoE SPA (Task 0).
 *
 * Four steps, one rule: **the server decides.** Every gate — engagement type,
 * allow list, evidence, independence, disclaimers, issuer eligibility — lives in
 * `core/attest` and is re-run on each call. This page holds form state and
 * renders whatever comes back. It never predicts a refusal and never
 * suppresses one.
 *
 * The calls themselves, and the state that is an answer rather than an input,
 * live in `useLetterCalls` — split out under the 400-line rule in
 * `docs/FILE_SIZE_POLICY.md`. What remains here is the form state, the step
 * machine, and the layout.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  fetchLetterTypes,
  fetchPolicy,
  type AttestPolicy,
  type LetterTypeSummary,
} from '@/components/attest/api'
import { AssertionPreview } from '@/components/attest/AssertionPreview'
import {
  ClassifyStep,
  EMPTY_CLASSIFY_FORM,
  type ClassifyFormState,
} from '@/components/attest/ClassifyStep'
import { IndependenceStep } from '@/components/attest/IndependenceStep'
import { LetterDetailsStep } from '@/components/attest/LetterDetailsStep'
import { LetterPreviewStep } from '@/components/attest/LetterPreviewStep'
import { DocumentsToGather } from '@/components/attest/RequiredDocumentsChecklist'
import {
  completedSteps,
  missingRequiredFields,
  type RequiredFieldContext,
  type RequiredFieldEntry,
} from '@/components/attest/requiredFields'
import { StepContinue } from '@/components/attest/StepGate'
import { useBasisMergedType } from '@/components/attest/useBasisMergedType'
import { useDemoPrefill } from '@/components/attest/useDemoPrefill'
import { useFieldAnchor } from '@/components/attest/useFieldAnchor'
import { useLetterCalls } from '@/components/attest/useLetterCalls'
import { readVerification } from '@/components/attest/verificationResult'
import {
  EMPTY_ISSUER_FORM,
  EMPTY_LETTER_FORM,
  type IssuerFormState,
  type LetterFormState,
} from '@/components/attest/formState'
import { WizardStepNav, type StepKey } from '@/components/attest/WizardStepNav'
import { formatApiError } from '@/utils/httpErrors'

export function LetterGenerator() {
  const [letterTypes, setLetterTypes] = useState<LetterTypeSummary[]>([])
  const [policy, setPolicy] = useState<AttestPolicy | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [step, setStep] = useState<StepKey>('classify')
  const [selectedTypeId, setSelectedTypeId] = useState('')
  // '' means "whatever the chosen type declares as its default basis".
  const [selectedBasis, setSelectedBasis] = useState('')
  const [classifyForm, setClassifyForm] = useState<ClassifyFormState>(EMPTY_CLASSIFY_FORM)
  const [letterForm, setLetterForm] = useState<LetterFormState>(EMPTY_LETTER_FORM)
  const [issuerForm, setIssuerForm] = useState<IssuerFormState>(EMPTY_ISSUER_FORM)

  useEffect(() => {
    void (async () => {
      try {
        const [types, pol] = await Promise.all([fetchLetterTypes(), fetchPolicy()])
        setLetterTypes(types)
        setPolicy(pol)
        if (types.length === 1) setSelectedTypeId(types[0].type_id)
      } catch (err) {
        setLoadError(formatApiError(err))
      }
    })()
  }, [])

  /** The type as the registry publishes it — the basis options live here. */
  const baseType = useMemo(
    () => letterTypes.find(t => t.type_id === selectedTypeId) ?? null,
    [letterTypes, selectedTypeId],
  )

  /** The type as the chosen basis rewrites it — see `useBasisMergedType`. */
  const letterType = useBasisMergedType(baseType, selectedBasis)

  /**
   * The server side of the wizard: the three calls, their answers, and the rule
   * that an answer does not outlive an edit to the inputs that produced it.
   */
  const {
    decision,
    classifyRefusal,
    artifact,
    letterRefusal,
    confirmed,
    setConfirmed,
    classifying,
    building,
    downloading,
    onClassify,
    onPreview,
    onDownload,
  } = useLetterCalls({
    letterType,
    selectedTypeId,
    selectedBasis,
    classify: classifyForm,
    form: letterForm,
    issuer: issuerForm,
  })

  /**
   * The engine's verdict on the evidence, mapped back onto the Step-3 rows
   * (ATTEST-EVIDENCE-REQUIRED-1 item 3).
   *
   * It rides on `artifact`/`letterRefusal` rather than on any state of its own,
   * which is what makes it self-expiring: `useLetterCalls` clears both the
   * moment the type, basis, issuer or form changes, so an edit takes the word
   * "verified" off the checklist without this page having to notice the edit.
   */
  const verification = useMemo(
    () => readVerification(letterForm.statements, artifact, letterRefusal),
    [letterForm.statements, artifact, letterRefusal],
  )

  /**
   * Everything the required-field derivation reads (ATTEST-UX-1 D1). Assembled
   * once here because it spans the whole wizard — the Step-5 preflight lists
   * empty step-1 answers too, and only this component holds all four forms.
   */
  const requiredCtx: RequiredFieldContext = useMemo(() => ({
    letterTypeId: selectedTypeId,
    letterType,
    classify: classifyForm,
    form: letterForm,
    issuer: issuerForm,
    policy,
  }), [selectedTypeId, letterType, classifyForm, letterForm, issuerForm, policy])

  /**
   * This step's own unfilled required fields, keyed by step (ATTEST-UX-2 D3).
   *
   * Not a second derivation — the same `missingRequiredFields` the preflight
   * and the asterisks run on, bucketed. `dormant` entries are already excluded
   * upstream, so an untouched statement row does not block a form the CPA has
   * only just opened, and `scope` never has entries, so Step 2 is never gated.
   */
  const missingByStep = useMemo(() => {
    const buckets: Record<StepKey, RequiredFieldEntry[]> = {
      classify: [], scope: [], details: [], independence: [], letter: [],
    }
    for (const entry of missingRequiredFields(requiredCtx)) {
      buckets[entry.step].push(entry)
    }
    return buckets
  }, [requiredCtx])

  /** The green ticks on the strip. Same derivation as the stars and the preflight. */
  const doneSteps = useMemo(() => completedSteps(requiredCtx), [requiredCtx])

  /**
   * "Go to step" / "Correct here" lands on the field, not the screen
   * (ATTEST-UX-2 D2). The queue-then-focus mechanism, and why it is a queue,
   * live in `useFieldAnchor`.
   */
  const queueFocus = useFieldAnchor(step)

  const goToStep = useCallback((next: StepKey, fieldAnchor?: string) => {
    setStep(next)
    queueFocus(fieldAnchor)
  }, [queueFocus])

  /** Same landing, without leaving the step the CPA is already on. */
  const focusField = queueFocus

  /** Changing type drops the basis: the old key means nothing to the new type. */
  const onSelectType = useCallback((id: string) => {
    setSelectedTypeId(id)
    setSelectedBasis('')
  }, [])

  /**
   * The worked example (ATTEST-UX-AND-DEMO-READINESS-1 item 2), or `undefined`
   * when it must not be offered — see `useDemoPrefill` for both conditions.
   *
   * It is wired from here because this is the only component holding every form
   * the example touches. It writes exactly the state a CPA would have typed and
   * bypasses nothing: `/classify` and `/letter` run every gate against it. That
   * the values clear those gates is proved by `tests/test_attest_demo_letter.py`,
   * which reads the same `demoLetter.json` and pushes it through `build_letter`.
   */
  const onLoadDemo = useDemoPrefill({
    letterTypes,
    classify: classifyForm,
    form: letterForm,
    setTypeId: setSelectedTypeId,
    setBasis: setSelectedBasis,
    setClassify: setClassifyForm,
    setForm: setLetterForm,
    setIssuer: setIssuerForm,
  })

  if (loadError) {
    return (
      <div className="p-6">
        <p className="text-destructive">{loadError}</p>
      </div>
    )
  }

  const canLeaveClassify = Boolean(letterType)

  return (
    <div className="max-w-5xl space-y-6 p-4 sm:p-6">
      <header>
        <h1 className="text-2xl font-semibold">Verification Letters</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Third-party verification letters, generated inside the boundary a CPA may
          actually sign. What the letter cannot say is enforced, shown, and explained.
        </p>
      </header>

      <WizardStepNav
        current={step}
        onSelect={setStep}
        canLeaveClassify={canLeaveClassify}
        completed={doneSteps}
      />

      {step === 'classify' && (
        <ClassifyStep
          letterTypes={letterTypes}
          selectedTypeId={selectedTypeId}
          onSelectType={onSelectType}
          basisChoice={baseType?.engagement_basis ?? null}
          selectedBasis={selectedBasis}
          onSelectBasis={setSelectedBasis}
          policy={policy}
          form={classifyForm}
          onFormChange={patch => setClassifyForm(prev => ({ ...prev, ...patch }))}
          decision={decision}
          refusal={classifyRefusal}
          busy={classifying}
          onClassify={() => void onClassify()}
          onContinue={() => setStep('scope')}
          missingRequired={missingByStep.classify}
          onCorrect={focusField}
          // Both, and they are not the same thing: `onCorrect` moves the cursor
          // inside step 1 for its own missing fields, while a refusal raised on
          // step 1 can be attributable to step 3 and needs the step to change.
          onGoToStep={goToStep}
          onLoadDemo={onLoadDemo}
        />
      )}

      {step === 'scope' && letterType && (
        <div className="space-y-4">
          <AssertionPreview letterType={letterType} policy={policy} />
          {/* Alongside what the letter may say, what it will want to see. Here
              rather than on Step 1 because it is the same "before you start
              writing" beat as the assertion preview, and because a CPA who
              learns the list one screen earlier can go and collect it. */}
          <DocumentsToGather rows={letterType.required_documents} />
          {/* Step 2 states no facts, so it has no required fields and can
              never gate. The component is used anyway so the one that can
              gate and the one that cannot behave identically. */}
          <StepContinue
            missing={missingByStep.scope}
            onContinue={() => setStep('details')}
            onCorrect={focusField}
            testId="scope-continue"
          />
        </div>
      )}

      {step === 'details' && letterType && (
        <div className="space-y-4">
          <LetterDetailsStep
            letterType={letterType}
            policy={policy}
            form={letterForm}
            verification={verification}
            /* The updater form is not cosmetic: nested patches (`inputs`,
               `statements`) must merge against the state React is about to
               apply them to, or two writes in one batch lose the first. */
            onChange={patch => setLetterForm(prev => ({
              ...prev,
              ...(typeof patch === 'function' ? patch(prev) : patch),
            }))}
          />
          <StepContinue
            missing={missingByStep.details}
            onContinue={() => setStep('independence')}
            onCorrect={focusField}
            testId="details-continue"
          />
        </div>
      )}

      {step === 'independence' && letterType && (
        <div className="space-y-4">
          <IndependenceStep
            letterType={letterType}
            issuer={issuerForm}
            policy={policy}
            subjectEntity={letterForm.subjectEntity}
            onChange={patch => setIssuerForm(prev => ({ ...prev, ...patch }))}
          />
          <StepContinue
            missing={missingByStep.independence}
            onContinue={() => setStep('letter')}
            onCorrect={focusField}
            testId="independence-continue"
          />
        </div>
      )}

      {step === 'letter' && letterType && (
        <LetterPreviewStep
          artifact={artifact}
          refusal={letterRefusal}
          busy={building}
          downloading={downloading}
          confirmed={confirmed}
          onToggleDisclaimer={(i, v) => setConfirmed(prev => ({ ...prev, [i]: v }))}
          onPreview={() => void onPreview()}
          onDownload={() => void onDownload()}
          preflight={requiredCtx}
          onGoToStep={goToStep}
        />
      )}

      {step !== 'classify' && !letterType && (
        <p className="text-sm text-muted-foreground">Select a letter type to continue.</p>
      )}
    </div>
  )
}

export default LetterGenerator
